import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs';
import { UserService } from '../../../shared-components/user/user.service';
import { DataQuery } from '../models/data-query.model';
import { DataQueryService } from '../data-query.service';
import { DataQueryWizardQueryInfoComponent } from './data-query-wizard-query-info/data-query-wizard-query-info.component';
import { DataQueryFieldsComponent } from '../data-query-fields/data-query-fields.component';
import { ErrorList } from '../../../shared-components/controls/error-prompt/error-list.model';
import { MatStepper } from '@angular/material/stepper';

@AutoUnsubscribe({ includeArrays: true })
@Component({
    selector: 'app-data-query-wizard',
    templateUrl: './data-query-wizard.component.html',
    styleUrls: ['./data-query-wizard.component.css']
})
export class DataQueryWizardComponent implements OnInit, AfterViewInit, OnDestroy {

    // Data Properties
    savedQueries: DataQuery[] = [];

    // State Properties
    currentStep = 0;
    currentQuery = new DataQuery();
    subscriptions: Subscription[] = [];
    errors: ErrorList[] = [];
    queryExists: boolean;

    // View Properties
    @ViewChild(DataQueryWizardQueryInfoComponent) queryInfoTab: DataQueryWizardQueryInfoComponent;
    @ViewChild(DataQueryFieldsComponent) fieldsTab: DataQueryFieldsComponent;
    @ViewChild(MatStepper) stepper: MatStepper;

    // Constructors
    constructor(
        private dataQueryService: DataQueryService,
        private dialogBox: MatDialogRef<DataQueryWizardComponent>,
        private userService: UserService,
        private dialog: MatDialog
    ) { }

    get queryStepCompleted(): boolean {
        return !!this.dataQueryService.selectedCoreFile;
    }

    get fieldStepCompleted(): boolean {
        return this.currentQuery.reportFields.length > 0;
    }

    // Event Functions
    ngOnInit(): void {
        // Resetting our state
        this.dataQueryService.selectedCoreFile = null;
        this.dataQueryService.selectedQuickStart = null;
        this.dataQueryService.selectedCoreFileFilterOptionId = null;
        this.dataQueryService.wizardOpen = true;

        // Obtaining data needed throughout the wizard
        this.dataQueryService.getSavedQueries().subscribe(savedQueries => {
            this.savedQueries = savedQueries;
        });

        // Subscribing to state changes
        this.subscriptions.push(
            this.dataQueryService.observableCurrentQueryStream$.subscribe(currentQuery => {
                this.currentQuery = currentQuery;
            })
        );
    }

    ngAfterViewInit(): void {
        // Currently this behavior isn't supported normally
        // https://stackoverflow.com/questions/55497836/angular-material-stepper-before-selection-change-prevent-step-change-for-some
        // https://github.com/angular/components/issues/9733
        this.stepper.steps.forEach((step, idx) => {
            step.select = () => { // We override each step to require validation before moving on
                if (idx < this.currentStep) {
                    this.errors = [];
                    this.currentStep = idx;
                } else if (idx > this.currentStep) {
                    let maxTab = 0;
                    if (this.queryStepCompleted) {
                        maxTab = 1;
                    }
                    if (this.fieldStepCompleted) {
                        maxTab = 3;
                    }
                    this.validateTab(
                        () => {
                            if (idx > maxTab) {
                                this.currentStep = maxTab;
                                this.validateTab();
                            } else {
                                this.currentStep = idx;
                            }
                        }
                    );
                }
            };
        });
    }

    ngOnDestroy(): void {
        this.dataQueryService.wizardOpen = false;
    }

    // UI Functions
    close() {
        // PRE: the close button was clicked
        // POST: closes the dialog
        this.dialogBox.close();
    }

    nextStep() {
        // PRE: the user clicks on the next arrow
        // POST: goes to the next step
        this.validateTab(
            () => {
                this.currentStep += 1;
                this.queryExists = true;
            }
        );
    }

    previousStep() {
        // PRE: the user clicks on the previous arrow
        // POST: goes to the previous step
        this.errors = [];
        this.currentStep -= 1;
    }

    stepChanged(event: StepperSelectionEvent) {
        // PRE: the user changed the stepper's step
        // POST: changes the content accordingly
        this.currentStep = event.selectedIndex;
        this.queryExists = true;
    }

    saveQuery() {
        // PRE: the user clicks "save" on the Results step
        // POST: saves the query and closes the dialog
        this.currentQuery.OwnerID = 1;
        const currentUser = this.userService.currentUser;
        this.currentQuery.OwnerName = currentUser.LastName
            + ', '
            + currentUser.FirstName
            + (currentUser.MiddleName ? ' ' + currentUser.MiddleName.slice(0, 1) : '');
        this.dataQueryService.observableWizardOpen.next(null);
        this.dialogBox.close();
    }

    // Other Functions
    validateTab(callback?: Function) {
        // POST: validates the current tab
        this.errors = [];
        switch (this.currentStep) {

            // Validing the query info
            case 0:

                // The core file is required
                if (!this.dataQueryService.selectedCoreFile) {
                    this.errors.push({
                        fieldName: 'Area',
                        fieldRef: this.queryInfoTab.coreFileField,
                        errorMsg: 'Required.'
                    });
                }

                // If we don't have errors, call the callback
                if (this.errors.length == 0 && callback) {
                    callback();
                }
                break;

            // Validating the Fields tab
            case 1:

                // The query requires at least one field
                if (this.currentQuery.reportFields.length == 0) {
                    this.errors.push({
                        fieldName: 'Shown Fields',
                        errorMsg: 'At least one shown field is required.',
                        fieldRef: null
                    });
                }

                // If we don't have errors, call the callback
                if (this.errors.length == 0 && callback) {
                    callback();
                }
                break;

            // Validating the Results tab
            case 3:
                this.dataQueryService.validateQuery(
                    callback,
                    (tab: string) => {
                        switch (tab) {
                            case 'Filters':
                                this.currentStep = 2;
                                break;
                            default:
                                this.currentStep = 1;
                                break;
                        }
                    }
                );
                break;

            // By default, just call the callback
            default:
                if (callback) {
                    callback();
                }
                break;
        }
    }
}
